import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Link
} from '@mui/material'
import {
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Settings as SetupIcon,
  Description as LogsIcon,
  Timeline as ReleaseIcon
} from '@mui/icons-material'
import { useTheme } from '../App'
import config from '../config'
import ApiService, { ProjectVersion } from '../services/apiService'
import { CategorizedRelease } from '../types/release'
import { prepareReleasesForTimelineWithCrossCategory } from '../services/releaseCategorizationService'
import ReleaseTimelineContainer from '../components/timeline/ReleaseTimelineContainer'
import { calculateExtendedTimelineRange } from '../utils/releaseDateFiltering'
import StandardAppBar from '../components/StandardAppBar'
import StandardBreadcrumbs from '../components/StandardBreadcrumbs'



const ReleasePage: React.FC = () => {
  const navigate = useNavigate()
  const { darkMode, toggleDarkMode } = useTheme()
  const [releases, setReleases] = useState<CategorizedRelease[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)


  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================



  // Calculate extended timeline range for scrolling (includes all releases)
  const timelineRange = useMemo(() => {
    return calculateExtendedTimelineRange(releases, new Date('2025-09-01'))
  }, [releases])

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================



  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    const fetchReleases = async (attempt: number = 1) => {
      try {
        setLoading(true)
        setError(null)

        // Fetch releases from API with timeout
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

        const response = await ApiService.getRedProjectReleases()
        const rawReleases = response.data
        clearTimeout(timeoutId)

        // Validate data
        if (!Array.isArray(rawReleases)) {
          throw new Error('Invalid data format received from API')
        }

        // Categorize and filter releases for timeline with cross-category support
        const categorizedReleases = prepareReleasesForTimelineWithCrossCategory(rawReleases, {
          minDate: new Date('2025-09-01'),
          filterUncategorized: false, // Show all releases initially
          sortBy: 'date'
        })

        // Debug: Log k8s releases specifically
        const k8sReleases = rawReleases.filter(r => r.name.toLowerCase().includes('k8s'))
        console.log('DEBUG: Raw k8s releases:', k8sReleases.map(r => r.name))

        const categorizedK8sReleases = categorizedReleases.filter(r => r.name.toLowerCase().includes('k8s'))
        console.log('DEBUG: Categorized k8s releases:', categorizedK8sReleases.map(r => r.name))

        // Find which release was lost during categorization
        const lostDuringCategorization = k8sReleases.filter(raw =>
          !categorizedK8sReleases.find(cat => cat.id === raw.id)
        )
        if (lostDuringCategorization.length > 0) {
          console.log('ERROR: Lost during categorization:', lostDuringCategorization.map(r => r.name))
        }

        setReleases(categorizedReleases)
        setRetryCount(0) // Reset retry count on success
      } catch (err) {
        console.error(`Error fetching releases (attempt ${attempt}):`, err)

        let errorMessage = 'Failed to fetch releases'

        if (err instanceof Error) {
          if (err.name === 'AbortError') {
            errorMessage = 'Request timed out. Please try again.'
          } else if (err.message.includes('fetch')) {
            errorMessage = 'Network error. Please check your connection and try again.'
          } else {
            errorMessage = err.message
          }
        }

        // Retry logic for network errors
        if (attempt < 3 && (err instanceof Error && (
          err.name === 'AbortError' ||
          err.message.includes('fetch') ||
          err.message.includes('network')
        ))) {
          console.log(`Retrying in ${attempt * 2} seconds...`)
          setTimeout(() => {
            setRetryCount(attempt)
            fetchReleases(attempt + 1)
          }, attempt * 2000) // Exponential backoff
        } else {
          setError(errorMessage)
          setLoading(false)
        }
      } finally {
        if (retryCount === 0) {
          setLoading(false)
        }
      }
    }

    fetchReleases()
  }, [])

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleReleaseClick = (release: CategorizedRelease) => {
    console.log('Release clicked:', release)
    // TODO: Navigate to release details or show modal
  }

  const handleSwimlaneHover = (category: string, isHovered: boolean) => {
    console.log(`Swimlane ${category} ${isHovered ? 'hovered' : 'unhovered'}`)
  }

  const handleRetry = () => {
    setRetryCount(0)
    setError(null)
    setLoading(true)
    // Trigger re-fetch by updating a dependency
    window.location.reload()
  }

  return (
    <div>
      <StandardAppBar
        darkMode={darkMode}
        toggleDarkMode={toggleDarkMode}
        onRefresh={handleRetry}
        isLoading={loading}
      />

      <Container maxWidth={false} sx={{ mt: 1, mb: '29px', pb: 0, maxWidth: '95vw' }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 2 }}>
          <StandardBreadcrumbs currentPage="Release Timeline" />
        </Box>

        {/* Error State */}
        {error && (
          <Paper sx={{ p: 1.5, mb: 2, borderRadius: '8px' }}>
            <Alert
              severity="error"
              sx={{ mb: 0 }}
              action={
                <Button color="inherit" size="small" onClick={handleRetry}>
                  Retry
                </Button>
              }
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error}
              </Typography>
              {retryCount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Attempted {retryCount} time{retryCount > 1 ? 's' : ''}
                </Typography>
              )}
            </Alert>
          </Paper>
        )}

        {/* Loading State */}
        {loading && (
          <Paper sx={{ p: 1.5, mb: 2, borderRadius: '8px' }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
              gap: 2
            }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {retryCount > 0 ? `Retrying... (attempt ${retryCount + 1})` : 'Loading releases...'}
              </Typography>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Release Timeline - Full Width */}
      {!loading && !error && (
        <Box sx={{
          width: '100vw',
          ml: 'calc(-50vw + 50%)', // Break out of container to use full viewport width
          mt: 0, // Clean separation from header
          mb: 3,
          pb: 8
        }}>
          <ReleaseTimelineContainer
            releases={releases}
            dateRange={timelineRange}
            onReleaseClick={handleReleaseClick}
            onSwimlaneHover={handleSwimlaneHover}
            showLabels={true}
            enableInteractions={true}
          />
        </Box>
      )}
    </div>
  )
}

export default ReleasePage
